/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";

import * as url from "helper/UrlHelper";
import { getData, postData, deleteData } from "utils/Gateway";

import WebsiteBuilderHeader from "../Header/WebsiteBuilderHeader";
import SavePageOffcanvas from "../Offcanvas/SavePageOffcanvas";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";

const WebsiteBuilderListBody = () => {
  const token = localStorage.getItem("token");
  const history = useHistory();
  const { t, i18n } = useTranslation();

  const [pageList, setPageList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const getAllPages = async () => {
    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_WEBSITE_PAGES + `?token=${token}`;
      const response = await getData(requestUrl);
      if (response.status) setPageList(response.data);
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishToggle = async (pageId) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_PUBLISH_WEBSITE_PAGE +
        `/${pageId}/publish?token=${token}`;
      const response = await postData(requestUrl, {});
      if (response.status) {
        setAlertMessage(response.message);
        setMessageType("success");
        setShowAlert(true);
        setReloadData(true);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm(t("Are you sure you want to delete this page?"))) return;
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_DELETE_WEBSITE_PAGE +
        `/${pageId}?token=${token}`;
      const response = await deleteData(requestUrl);
      if (response.status) {
        setAlertMessage(response.message);
        setMessageType("success");
        setShowAlert(true);
        setReloadData(true);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  const handleUpdateClick = (page) => {
    setSelectedPage(page);
    const el = document.getElementById("savePageOffcanvas");
    const bsOffcanvas =
      window.bootstrap && window.bootstrap.Offcanvas.getOrCreateInstance(el);
    if (bsOffcanvas) bsOffcanvas.show();
  };

  const onPageSaved = () => {
    setAlertMessage(
      selectedPage ? t("Page updated successfully") : t("Page created successfully")
    );
    setMessageType("success");
    setShowAlert(true);
    setReloadData(true);
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("Page"),
        size: 240,
        Cell: ({ row }) => (
          <div>
            <p className="fw-semibold mb-0">{row.original.name}</p>
            <p className="text-muted small mb-0">
              <span className="material-symbols-outlined icon-xs me-1">link</span>
              /{row.original.slug}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "company",
        header: t("Company"),
        size: 180,
        Cell: ({ row }) => (
          <div>
            <p className="mb-0 small">
              {row.original.company?.name || <span className="text-muted">—</span>}
            </p>
            {row.original.companycif && (
              <p className="text-muted small mb-0">
                <span className="material-symbols-outlined icon-xs me-1">tag</span>
                {row.original.companycif}
              </p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "baseurl",
        header: t("Domain"),
        size: 180,
        Cell: ({ row }) =>
          row.original.baseurl ? (
            <a
              href={row.original.baseurl}
              target="_blank"
              rel="noreferrer"
              className="text-primary small text-truncate d-block"
              style={{ maxWidth: 160 }}
            >
              {row.original.baseurl}
            </a>
          ) : (
            <span className="text-muted small">—</span>
          ),
      },
      {
        accessorKey: "status",
        header: t("Status"),
        size: 120,
        Cell: ({ row }) => (
          <span
            className={`badge rounded-pill px-2 py-1 small ${
              row.original.status === "published"
                ? "bg-success-subtle text-success"
                : "bg-secondary-subtle text-secondary"
            }`}
          >
            {row.original.status === "published" ? t("Published") : t("Draft")}
          </span>
        ),
      },
      {
        accessorKey: "updatedAt",
        header: t("Last Updated"),
        size: 140,
        Cell: ({ row }) => (
          <span className="text-muted small">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [i18n.language]
  );

  useEffect(() => {
    getAllPages();
  }, []);

  useEffect(() => {
    if (reloadData) {
      getAllPages();
      setReloadData(false);
    }
  }, [reloadData]);

  return (
    <div id="content_wrapper">
      <section className="crm-wrapper bg-white pb-5">
        <div className="container-fluid px-lg-5">
          <WebsiteBuilderHeader reloadPageList={() => setReloadData(true)} />

          <MaterialReactTable
            columns={columns}
            data={pageList}
            state={{ isLoading, rowSelection }}
            enableGrouping
            enableRowSelection
            getRowId={(row) => row._id}
            onRowSelectionChange={setRowSelection}
            enableRowActions
            positionActionsColumn="first"
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: "4px" }}>
                <button
                  className="btn btn-sm btn-outline-secondary border-0 p-1"
                  title={t("Update")}
                  onClick={() => handleUpdateClick(row.original)}
                >
                  <span className="material-symbols-outlined icon-sm">edit</span>
                </button>
                <Link
                  to={`/admin/websitebuilder/editor/${row.original._id}`}
                  className="btn btn-sm btn-outline-secondary border-0 p-1"
                  title={t("Open Editor")}
                >
                  <span className="material-symbols-outlined icon-sm">open_in_new</span>
                </Link>
              </Box>
            )}
            muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
            enableStickyHeader
            renderEmptyRowsFallback={() => (
              <div className="text-center py-5">
                <span
                  className="material-symbols-outlined text-muted"
                  style={{ fontSize: 64 }}
                >
                  web
                </span>
                <h5 className="mt-3 text-muted">{t("No pages yet")}</h5>
                <p className="text-muted small">
                  {t("Create your first landing page using the AI chat builder.")}
                </p>
                <button
                  className="btn btn-primary px-4 py-2 rounded-3 mt-2"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#savePageOffcanvas"
                >
                  <span className="material-symbols-outlined icon-md me-1">add</span>
                  {t("Create First Page")}
                </button>
              </div>
            )}
          />
        </div>
      </section>

      {/* New / Update Page Offcanvas */}
      <SavePageOffcanvas
        selectedPage={selectedPage}
        onSaved={onPageSaved}
        onClose={() => setSelectedPage(null)}
      />

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={() => setShowAlert(false)}
        />
      )}
    </div>
  );
};

export default WebsiteBuilderListBody;
